import * as THREE from 'three';

const CONTACT_TOLERANCE = 1;

// ignoreOverlap families may legitimately share a cabinet's footprint, but
// separation cannot read that flag, so it excludes them by type instead.
const isSolidBody = (product: THREE.Object3D): boolean => {
    const data = product?.userData ?? {};

    return !(
        data.isGhostPanel ||
        data.isFillerProduct ||
        data.isVerminProduct ||
        data.isManualPanel ||
        data.isKickerLengthProduct ||
        data.isNonManualPanel
    );
};

type Escape = {axis: 'x' | 'z'; shift: number};

// The four ways out of an overlap, nearest first. The first is the
// minimum-translation push along the shallower axis, so a panel slides along
// the neighbour's face; the rest are the fallbacks when that one is refused.
const escapesFrom = (box: THREE.Box3, otherBox: THREE.Box3): Escape[] => {
    const escapes: Escape[] = [
        {axis: 'x', shift: otherBox.max.x - box.min.x},
        {axis: 'x', shift: otherBox.min.x - box.max.x},
        {axis: 'z', shift: otherBox.max.z - box.min.z},
        {axis: 'z', shift: otherBox.min.z - box.max.z},
    ];

    return escapes.sort((a, b) => Math.abs(a.shift) - Math.abs(b.shift));
};

// `isPlacementAllowed` rejects a push that would leave the panel somewhere it
// may not sit, such as through the notch wall of an L-shaped room, which the
// room clamp cannot pull it back across.
// One neighbour per call; a drag converges over frames.
export const separateFromProducts = (
    group: THREE.Object3D,
    products: THREE.Object3D[],
    isPlacementAllowed: (candidateBounds: THREE.Box3) => boolean = () => true
): THREE.Vector3 | null => {
    if (!products?.length) return null;

    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    const groupId = group.userData?.id as unknown;

    const otherBox = new THREE.Box3();
    const deepestOtherBox = new THREE.Box3();

    let deepestDepth = 0;

    for (const product of products) {
        if (product === group) continue;

        const productId = product.userData?.id as unknown;
        if (productId !== undefined && productId === groupId) continue;
        if (!isSolidBody(product)) continue;

        otherBox.setFromObject(product);

        const overlapY =
            Math.min(box.max.y, otherBox.max.y) -
            Math.max(box.min.y, otherBox.min.y);
        if (overlapY <= CONTACT_TOLERANCE) continue;

        const overlapX =
            Math.min(box.max.x, otherBox.max.x) -
            Math.max(box.min.x, otherBox.min.x);
        const overlapZ =
            Math.min(box.max.z, otherBox.max.z) -
            Math.max(box.min.z, otherBox.min.z);
        if (overlapX <= CONTACT_TOLERANCE || overlapZ <= CONTACT_TOLERANCE) {
            continue;
        }

        // Resolve the neighbour needing the largest correction first. Both
        // overlaps clear CONTACT_TOLERANCE here, so any depth is a neighbour.
        const depth = Math.min(overlapX, overlapZ);
        if (depth > deepestDepth) {
            deepestDepth = depth;
            deepestOtherBox.copy(otherBox);
        }
    }

    if (!deepestDepth) return null;

    const candidate = new THREE.Box3();

    for (const escape of escapesFrom(box, deepestOtherBox)) {
        candidate.copy(box);
        candidate.min[escape.axis] += escape.shift;
        candidate.max[escape.axis] += escape.shift;
        if (!isPlacementAllowed(candidate)) continue;

        const applied = new THREE.Vector3();
        applied[escape.axis] = escape.shift;
        group.position[escape.axis] += escape.shift;
        group.updateMatrixWorld(true);

        return applied;
    }

    // Every way out is refused, so leave the panel touching its neighbour
    // rather than shoving it somewhere it may not sit.
    return null;
};
